import { memo } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DataObjectOutlinedIcon from "@mui/icons-material/DataObjectOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import { alpha, useTheme } from "@mui/material/styles";

import { getMessageTemplateVariableLabel } from "../utils/fluxmedDefaultTemplateVariables";

export interface MessageTemplateVariablesPickerProps {
  variableNames: readonly string[];
  activeFieldLabel: string;
  onInsertVariable: (variableName: string) => void;
  isDefaultTemplate?: boolean;
}

const MessageTemplateVariablesPicker = memo(
  ({
    variableNames,
    activeFieldLabel,
    onInsertVariable,
    isDefaultTemplate = false,
  }: MessageTemplateVariablesPickerProps) => {
    const theme = useTheme();

    if (variableNames.length === 0) {
      return null;
    }

    return (
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "background.paper",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <DataObjectOutlinedIcon
              color="primary"
              fontSize="small"
              sx={{ mt: 0.25 }}
            />
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                Variáveis do template
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isDefaultTemplate
                  ? "Lista deste modelo padrão."
                  : "Toque em uma variável para inserir no texto."}
              </Typography>
            </Box>
          </Stack>
          <Chip
            icon={<TouchAppOutlinedIcon sx={{ fontSize: "1rem !important" }} />}
            label={activeFieldLabel}
            size="small"
            color="primary"
            variant="outlined"
            sx={{
              maxWidth: "100%",
              height: "auto",
              py: 0.25,
              "& .MuiChip-label": {
                whiteSpace: "normal",
                lineHeight: 1.3,
              },
            }}
          />
        </Stack>

        <Box
          sx={{
            p: 1.5,
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: 1,
          }}
        >
          {variableNames.map((variableName) => {
            const label = getMessageTemplateVariableLabel(variableName);
            const token = `{{${variableName}}}`;

            return (
              <ButtonBase
                key={variableName}
                onClick={() => onInsertVariable(variableName)}
                focusRipple
                aria-label={`Inserir variável ${label}`}
                sx={{
                  width: "100%",
                  display: "block",
                  textAlign: "left",
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.default",
                  transition: theme.transitions.create(
                    ["border-color", "box-shadow", "background-color"],
                    { duration: theme.transitions.duration.shorter },
                  ),
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                  "&:focus-visible": {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ p: 1.25, width: "100%" }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="text.primary"
                      noWrap
                      title={label}
                    >
                      {label}
                    </Typography>
                    <Typography
                      component="code"
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.25,
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        color: "primary.main",
                        fontWeight: 500,
                        letterSpacing: 0.2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={token}
                    >
                      {token}
                    </Typography>
                  </Box>
                  <AddCircleOutlineIcon
                    fontSize="small"
                    color="primary"
                    sx={{ flexShrink: 0, opacity: 0.7 }}
                  />
                </Stack>
              </ButtonBase>
            );
          })}
        </Box>
      </Box>
    );
  },
);

MessageTemplateVariablesPicker.displayName = "MessageTemplateVariablesPicker";

export default MessageTemplateVariablesPicker;
